import React, { useState, useEffect, useRef } from "react";
import { Box, IconButton, Button, Typography, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { alpha } from "@mui/material/styles";
import BackdropOverlay from "../common/BackdropOverlay.jsx";
import { OUTLINED_INPUT_SX } from "../common/outlinedInputStyles";
import { DIALOG_CANCEL_BUTTON_SX, DIALOG_PAPER_RADIUS } from "../common/dialogActionButtonSx";
import { ACCOUNT_PAGE_KYC_CTA_SX } from "../account/accountPageStyles";
import { MOBILE_NAV_MEDIA } from "./layoutConstants";
import { pushToast } from "../../stores/toastStore";

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

const DIALOG_FADE_IN = {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 },
};

export default function JoinPartyDialog({
    open,
    onCancel,
    joinLobbyByCode,
    lobbyCodeNotFound,
    lobbyFull,
}) {

    const [lobbyCode, setLobbyCode] = useState('');
    const prevErrorRef = useRef({ lobbyCodeNotFound: false, lobbyFull: false });

    useEffect(() => {
        const prev = prevErrorRef.current;
        if (lobbyCodeNotFound && !prev.lobbyCodeNotFound) {
            pushToast({ type: "error", message: "Invalid party code" });
        }
        if (lobbyFull && !prev.lobbyFull) {
            pushToast({ type: "error", message: "The party is full" });
        }
        prevErrorRef.current = { lobbyCodeNotFound, lobbyFull };
    }, [lobbyCodeNotFound, lobbyFull]);

    if (!open) return null;

    const handleConfirm = () => {
        if (lobbyCode.trim()) {
            joinLobbyByCode(lobbyCode.trim());
            setLobbyCode('');
        }
    };

    const handleCancel = () => {
        setLobbyCode('');
        onCancel();
    };

    return (
        <BackdropOverlay open>
            <Box
                role="dialog"
                sx={{
                    "@keyframes dialogFadeIn": { ...DIALOG_FADE_IN },
                    position: 'relative',
                    width: { xs: '100%', sm: '27.5rem', xl: '28.5rem' },
                    maxWidth: '28.5rem',
                    overflow: "hidden",
                    background: `
                        linear-gradient(
                            165deg,
                            rgba(18,14,14,0.98) 0%,
                            rgba(10,10,12,0.99) 48%,
                            rgba(8,8,10,1) 100%
                        )
                    `,
                    borderRadius: DIALOG_PAPER_RADIUS,
                    border: `1px solid ${colors.border.default}`,
                    boxShadow: '0 28px 64px rgba(0, 0, 0, 0.55)',
                    animation: 'dialogFadeIn 0.2s ease-in-out',
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "12%",
                        right: "12%",
                        height: "1px",
                        background: `linear-gradient(90deg, transparent, ${alpha(colors.accent.main, 0.55)}, transparent)`,
                        pointerEvents: "none",
                    },
                }}
            >
                <IconButton
                    onClick={handleCancel}
                    aria-label="Close join party dialog"
                    sx={{
                        position: 'absolute',
                        top: '0.8rem',
                        right: '0.8rem',
                        width: '1.9rem',
                        height: '1.9rem',
                        borderRadius: '0.55rem',
                        color: colors.text.secondary,
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                    }}
                >
                    <Close sx={{ fontSize: '0.95rem' }} />
                </IconButton>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        px: { xs: '1.35rem', sm: '1.6rem' },
                        pt: '1.55rem',
                        pb: '1.4rem',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: { xs: '1.35rem', xl: '1.5rem' },
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            color: colors.text.primary,
                        }}
                    >
                        Join Party
                    </Typography>

                    <Typography
                        sx={{
                            mt: '0.75rem',
                            maxWidth: '24rem',
                            fontSize: { xs: '0.88rem', xl: '0.95rem' },
                            lineHeight: 1.5,
                            color: colors.text.secondary,
                        }}
                    >
                        Enter the code shared by your teammate to join their party and queue together.
                    </Typography>

                    <Box
                        sx={{
                            width: '100%',
                            marginTop: { xs: '1.7rem', xl: '1.9rem' },
                            marginBottom: { xs: '1rem', xl: '1.15rem' },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <TextField
                            sx={{
                                ...OUTLINED_INPUT_SX,
                                "& .MuiOutlinedInput-input": {
                                    ...OUTLINED_INPUT_SX["& .MuiOutlinedInput-input"],
                                    padding: "0.72rem 0.85rem",
                                },
                            }}
                            variant="outlined"
                            placeholder="Enter party code (e.g. K7M2Q9X4PL)"
                            value={lobbyCode}
                            onChange={(e) => setLobbyCode(e.target.value.toUpperCase())}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && lobbyCode.trim()) {
                                    e.preventDefault();
                                    handleConfirm();
                                }
                            }}
                            autoFocus
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.75rem',
                            mt: '0.55rem',
                            width: '100%',
                            maxWidth: '22rem',
                            [MOBILE_MQ]: {
                                flexDirection: 'column',
                            },
                        }}
                    >
                        <Button
                            onClick={handleCancel}
                            sx={{
                                ...DIALOG_CANCEL_BUTTON_SX,
                                flex: { xs: "none", sm: "0 0 9.5rem" },
                                width: { sm: "9.5rem" },
                                minWidth: { sm: "9.5rem" },
                                maxWidth: { sm: "9.5rem" },
                                [MOBILE_MQ]: {
                                    order: 0,
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            disabled={!lobbyCode.trim()}
                            sx={{
                                ...ACCOUNT_PAGE_KYC_CTA_SX,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flex: { xs: "none", sm: "0 0 11.75rem" },
                                width: { sm: "11.75rem" },
                                minWidth: { sm: "11.75rem" },
                                maxWidth: { sm: "11.75rem" },
                                minHeight: "2.5rem",
                                height: "2.5rem",
                                px: "1rem",
                                fontSize: "0.76rem",
                                fontWeight: 750,
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                [MOBILE_MQ]: {
                                    order: 1,
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    minHeight: "2.35rem",
                                    height: "2.35rem",
                                    borderRadius: "6px",
                                },
                            }}
                        >
                            Join Party
                        </Button>
                    </Box>
                </Box>
            </Box>
        </BackdropOverlay>
    );
}
